/**
 * Centralized Logging Utility with Trace ID Support
 *
 * Provides consistent logging across the frontend with:
 * - Trace IDs for end-to-end correlation (frontend <-> backend)
 * - Structured event logging for analytics and debugging
 * - Log levels (debug, info, warn, error)
 * - Debug logs gated behind environment variable
 *
 * Usage:
 *   import { createLogger, getTraceId, logEvent } from "@/app/lib/logger";
 *
 *   const log = createLogger("MyComponent");
 *   log.info("Something happened", { userId: 123 });
 *   log.error("Failed to fetch", error, { endpoint: "/api/users" });
 *
 *   // Structured business event
 *   logEvent("job.creation.completed", { job_id: "123", duration_ms: 5000 });
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

interface Logger {
  debug: (message: string, context?: LogContext) => void;
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, error?: Error | unknown, context?: LogContext) => void;
}

// ============================================
// TRACE ID MANAGEMENT
// ============================================

const TRACE_ID_HEADER = "X-Trace-ID";
const TRACE_ID_KEY = "trace_id";

/**
 * Generate a new trace ID
 * Format: timestamp-random (e.g., "1706284800000-a1b2c3d4")
 */
function generateTraceId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${random}`;
}

// Session-scoped trace ID (persists across navigations within session)
let sessionTraceId: string | null = null;

/**
 * Get or create the current session's trace ID.
 * The trace ID persists for the browser session.
 */
export function getTraceId(): string {
  if (typeof window === "undefined") {
    // Server-side: generate new trace ID per request
    return generateTraceId();
  }

  if (!sessionTraceId) {
    // Try to restore from sessionStorage
    try {
      sessionTraceId = sessionStorage.getItem(TRACE_ID_KEY);
      if (!sessionTraceId) {
        sessionTraceId = generateTraceId();
        sessionStorage.setItem(TRACE_ID_KEY, sessionTraceId);
      }
    } catch {
      // sessionStorage not available (SSR, private mode, etc.)
      sessionTraceId = generateTraceId();
    }
  }

  return sessionTraceId;
}

/**
 * Create a new trace ID for a specific operation/flow.
 * Use this when you want to track a specific user action across systems.
 */
export function createTraceId(): string {
  return generateTraceId();
}

/**
 * Add trace ID to request headers
 */
export function withTraceId(headers: HeadersInit = {}): HeadersInit {
  return {
    ...headers,
    [TRACE_ID_HEADER]: getTraceId(),
  };
}

/**
 * Header name for trace ID (for backend to read)
 */
export { TRACE_ID_HEADER };

// ============================================
// LOGGER CONFIGURATION
// ============================================

// Debug logging enabled via environment variable
const DEBUG_ENABLED =
  typeof process !== "undefined" &&
  process.env.NODE_ENV === "development";

// Predefined module prefixes for consistency
export const LogModules = {
  // Analytics & Tracking
  TRACKING: "Tracking",
  GA4_CLIENT: "GA4 Client",
  META_CLIENT: "Meta Client",
  SERVER_SIDE: "Server-Side",
  ANALYTICS: "Analytics",

  // Core Features
  AUTH: "Auth",
  API: "API",
  FORM: "Form",
  FUNNEL: "Funnel",

  // Dashboard
  DASHBOARD: "Dashboard",
  JOBS: "Jobs",
  SEARCH: "Search",

  // Admin
  ADMIN: "Admin",

  // UI/Components
  COMPONENT: "Component",
  HOOK: "Hook",

  // System
  I18N: "i18n",
  SESSION: "Session",
  STRIPE: "Stripe",
} as const;

type LogModule = (typeof LogModules)[keyof typeof LogModules] | string;

// ============================================
// FORMATTING
// ============================================

/**
 * Format log message with module prefix and trace ID
 */
function formatMessage(module: LogModule, message: string, includeTraceId: boolean = true): string {
  if (includeTraceId && typeof window !== "undefined") {
    const traceId = getTraceId();
    const shortTraceId = traceId.split("-")[1] || traceId.substring(0, 8);
    return `[${module}] [${shortTraceId}] ${message}`;
  }
  return `[${module}] ${message}`;
}

/**
 * Extract error details for logging
 */
function formatError(error: Error | unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    };
  }
  return {
    message: String(error),
  };
}

// ============================================
// LOGGER FACTORY
// ============================================

/**
 * Create a logger instance for a specific module
 */
export function createLogger(module: LogModule): Logger {
  return {
    debug: (message: string, context?: LogContext) => {
      if (!DEBUG_ENABLED) return;
      const formatted = formatMessage(module, message);
      if (context) {
        console.debug(formatted, context);
      } else {
        console.debug(formatted);
      }
    },

    info: (message: string, context?: LogContext) => {
      const formatted = formatMessage(module, message);
      if (context) {
        console.log(formatted, context);
      } else {
        console.log(formatted);
      }
    },

    warn: (message: string, context?: LogContext) => {
      const formatted = formatMessage(module, message);
      if (context) {
        console.warn(formatted, context);
      } else {
        console.warn(formatted);
      }
    },

    error: (message: string, error?: Error | unknown, context?: LogContext) => {
      const formatted = formatMessage(module, message);
      const errorDetails = error ? formatError(error) : null;

      if (errorDetails && context) {
        console.error(formatted, { error: errorDetails, ...context });
      } else if (errorDetails) {
        console.error(formatted, errorDetails);
      } else if (context) {
        console.error(formatted, context);
      } else {
        console.error(formatted);
      }
    },
  };
}

// ============================================
// PRE-CONFIGURED LOGGERS
// ============================================

/**
 * Pre-configured loggers for common modules
 * Import these directly for convenience
 */
export const trackingLog = createLogger(LogModules.TRACKING);
export const ga4Log = createLogger(LogModules.GA4_CLIENT);
export const metaLog = createLogger(LogModules.META_CLIENT);
export const serverSideLog = createLogger(LogModules.SERVER_SIDE);
export const apiLog = createLogger(LogModules.API);
export const authLog = createLogger(LogModules.AUTH);
export const formLog = createLogger(LogModules.FORM);
export const dashboardLog = createLogger(LogModules.DASHBOARD);

// ============================================
// STRUCTURED EVENT LOGGING
// ============================================

/**
 * Structured event data for business events.
 * Use dot notation for event names: domain.entity.action[.outcome]
 *
 * Examples:
 *   job.creation.completed
 *   applicant.form.submitted
 *   payment.subscription.failed.card_declined
 */
interface EventData {
  // Outcome
  outcome?: "success" | "failure" | "pending";

  // Business context - include ALL relevant IDs in ONE log entry
  job_id?: string;
  landing_page_id?: string;
  applicant_id?: string;
  user_id?: string;

  // Metrics
  duration_ms?: number;
  count?: number;

  // Error details (for failures)
  error_type?: string;
  error_message?: string;
  recoverable?: boolean;

  // Any additional context
  [key: string]: unknown;
}

/**
 * Log a structured business event.
 *
 * ANTI-PATTERN (multiple scattered logs):
 *   log.info("Starting job creation");
 *   log.info("Job ID: 123");
 *   log.info("Job creation completed");
 *
 * CORRECT (single complete log):
 *   logEvent("job.creation.completed", {
 *     job_id: "123",
 *     user_id: "456",
 *     duration_ms: 5000,
 *     outcome: "success"
 *   });
 *
 * @param event - Dot notation event name (e.g., "job.creation.completed")
 * @param data - All relevant context in one object
 */
export function logEvent(event: string, data: EventData = {}): void {
  const traceId = typeof window !== "undefined" ? getTraceId() : "server";
  const shortTraceId = traceId.split("-")[1] || traceId.substring(0, 8);

  // Determine log level based on outcome
  const level = data.outcome === "failure" ? "error" : "info";
  const logFn = level === "error" ? console.error : console.log;

  // Single structured log entry
  logFn(`[Event] [${shortTraceId}] ${event}`, {
    trace_id: traceId,
    timestamp: new Date().toISOString(),
    ...data,
  });
}

/**
 * Create a timer for measuring operation duration.
 * Returns a function that logs the event with duration when called.
 *
 * Usage:
 *   const done = startTimer("job.creation");
 *   // ... do work ...
 *   done({ job_id: "123", outcome: "success" });
 *
 * This logs: [Event] job.creation.completed { duration_ms: 5000, job_id: "123", ... }
 */
export function startTimer(
  eventPrefix: string
): (data?: EventData & { outcome?: "success" | "failure" }) => void {
  const startTime = Date.now();

  return (data: EventData & { outcome?: "success" | "failure" } = {}) => {
    const duration_ms = Date.now() - startTime;
    const outcome = data.outcome || "success";
    const suffix = outcome === "success" ? "completed" : "failed";

    logEvent(`${eventPrefix}.${suffix}`, {
      ...data,
      duration_ms,
      outcome,
    });
  };
}
